import React, { useEffect, useRef, useState } from 'react';

interface TypeHeadlineProps {
  text: string;
  /** milliseconds per character */
  speedMs?: number;
  className?: string;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Console-style typed headline. The FULL text is always in the DOM
 * (accessibility + SEO); the typing animation only controls how much
 * is visually revealed. Users with prefers-reduced-motion see the
 * finished headline immediately.
 */
const TypeHeadline: React.FC<TypeHeadlineProps> = ({
  text,
  speedMs = 45,
  className = '',
}) => {
  const [shown, setShown] = useState(prefersReducedMotion() ? text.length : 0);
  const frame = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setShown(text.length);
      return;
    }
    let i = 0;
    let last = performance.now();
    const tick = (now: number) => {
      while (now - last >= speedMs && i < text.length) {
        i += 1;
        last += speedMs;
      }
      setShown(i);
      if (i < text.length) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [text, speedMs]);

  const done = shown >= text.length;
  return (
    <h1 className={`type-headline ${className}`} aria-label={text}>
      {/* full text for assistive tech and crawlers */}
      <span className="sr-full">{text}</span>
      <span aria-hidden="true" className="type-visible">
        {text.slice(0, shown)}
        <span className={`type-cursor${done ? ' done' : ''}`}>▮</span>
      </span>
    </h1>
  );
};

export default TypeHeadline;
